import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useCountry } from "@/lib/country";
import { SUPPORTED_COUNTRIES, countryMeta } from "@/lib/countries";
import {
    HeartHandshake, ArrowLeft, Loader2, Sparkles, ExternalLink, MapPin, Tag,
    Brain, Flower2, Dumbbell, Stethoscope, UtensilsCrossed, Bed, Users, Church, Heart, Globe,
} from "lucide-react";

/* Caregiver-burnout categories — what Memory Lane is actively working on
 * lining up. Order matches CommunityPartners backend (CATEGORIES dict) so
 * the "what's coming for you" tiles stay in sync with the actual data.
 * Each tile is intentionally short and warm — no bullet-points, no fluff. */
const COMING_CATEGORIES = [
    { id: "mental_wellness", label: "Mental wellness", Icon: Brain,
      tagline: "Therapy, coaching, and grief support — built for caregivers, not for crisis hotlines." },
    { id: "spa_self_care", label: "Spas & self-care", Icon: Flower2,
      tagline: "Massages, facials, sound baths — 60 minutes where someone else takes care of you." },
    { id: "fitness", label: "Gyms & fitness", Icon: Dumbbell,
      tagline: "Drop-in gym passes and short, doable workouts that fit between visits and appointments." },
    { id: "virtual_doctor", label: "Virtual doctors", Icon: Stethoscope,
      tagline: "Same-day visits for YOU — because you can't pour from an empty cup, or run a fever, alone." },
    { id: "meals", label: "Meal services", Icon: UtensilsCrossed,
      tagline: "Heart-healthy meals delivered ready-to-heat — for them, and the nights you just can't cook." },
    { id: "respite", label: "Respite care", Icon: Bed,
      tagline: "Vetted respite providers who actually pick up the phone — even at short notice." },
    { id: "support_group", label: "Support groups", Icon: Users,
      tagline: "Peer-led circles, in person and virtual, where 'I get it' isn't a polite phrase." },
    { id: "faith", label: "Faith communities", Icon: Church,
      tagline: "Local congregations and chaplaincy programs that walk this road with you, however you pray." },
];

/* CommunityPartners (caregiver-facing)
 * --------------------------------------
 * Read-only directory of partners that help reduce caregiver burnout:
 * spas, gyms, mental wellness, virtual doctors, meal services, respite,
 * faith communities, support groups.
 *
 * Data comes from GET /api/community-partners (admin-curated). When the
 * list is empty (which it will be at first), the page leans into the
 * "coming soon" promise instead of looking broken.
 */
export default function CommunityPartners() {
    const { country: userCountry, setCountry } = useCountry();
    const [country, setLocalCountry] = useState(userCountry || "US");
    const [loading, setLoading] = useState(true);
    const [partners, setPartners] = useState([]);
    const [categories, setCategories] = useState([]);
    const [selected, setSelected] = useState("all");
    const partnersRef = React.useRef(null);

    // Keep our local viewer-country in sync if user changes it globally.
    useEffect(() => {
        if (userCountry && userCountry !== country) setLocalCountry(userCountry);
    }, [userCountry]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        let active = true;
        setLoading(true);
        api.get(`/community-partners?country=${encodeURIComponent(country)}`).then((r) => {
            if (!active) return;
            setPartners(r.data.partners || []);
            setCategories(r.data.categories || []);
        }).catch(() => {
            // Silent — show the empty state.
        }).finally(() => {
            if (active) setLoading(false);
        });
        return () => { active = false; };
    }, [country]);

    const onPickCountry = (newCode) => {
        setLocalCountry(newCode);
        // Persist the choice on the user record so other pages (Documents,
        // future country-aware views) pick it up too.
        try { setCountry(newCode); } catch { /* anon viewer — local only */ }
    };

    const countryLabel = countryMeta(country)?.label || country;
    const countryFlag = countryMeta(country)?.flag || "";

    const filtered = useMemo(() => {
        if (selected === "all") return partners;
        if (selected === "coming_soon") return partners.filter(p => p.status === "coming_soon");
        if (selected === "live") return partners.filter(p => p.status === "live");
        return partners.filter(p => p.category === selected);
    }, [partners, selected]);

    const counts = {
        live: partners.filter(p => p.status === "live").length,
        coming_soon: partners.filter(p => p.status === "coming_soon").length,
    };

    return (
        <div
            className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-12 animate-fade-in-up"
            data-testid="community-partners-page"
        >
            <Link
                to="/resources"
                className="text-sm text-teal-dark hover:underline inline-flex items-center gap-1.5 mb-6"
            >
                <ArrowLeft size={14} strokeWidth={1.8} /> Back to Resources
            </Link>

            <header className="mb-8 max-w-3xl">
                <p className="text-xs uppercase tracking-wider font-semibold text-teal-dark mb-2 inline-flex items-center gap-1.5">
                    <HeartHandshake size={14} strokeWidth={1.8} /> Community partners
                </p>
                <h1 className="font-heading text-4xl md:text-5xl font-medium tracking-tight text-[#12776F]">
                    People in your corner — for <em className="italic font-normal">you</em>.
                </h1>
                <p className="mt-3 text-base md:text-lg text-[#4C5551] leading-relaxed">
                    Caregiving is a job no one prepares you for. These are the spas, gyms,
                    mental-wellness providers, virtual doctors, meal services, and respite
                    options Ashlee is partnering with — so the next time you&apos;re running on
                    empty, there&apos;s already a soft place to land.
                </p>
                <p className="mt-3 text-sm text-stage_early-dark inline-flex items-center gap-1.5">
                    <Sparkles size={14} strokeWidth={1.8} /> Many are still rolling out.
                    Tap "Coming soon" to peek at what&apos;s on the way.
                </p>

                {/* Country picker — partners differ by country. Default to the
                    user's saved country; let them switch for travel / family
                    in a different place. */}
                <div className="mt-4 inline-flex items-center gap-2 flex-wrap" data-testid="community-partners-country-picker">
                    <span className="text-xs uppercase tracking-wider font-semibold text-[#6E7A74] inline-flex items-center gap-1.5">
                        <Globe size={12} strokeWidth={1.8} /> Showing partners in:
                    </span>
                    <label className="sr-only" htmlFor="cp-country">Country</label>
                    <select
                        id="cp-country"
                        value={country}
                        onChange={(e) => onPickCountry(e.target.value)}
                        data-testid="community-partners-country-select"
                        className="px-3 py-1.5 rounded-2xl border border-cream-dark bg-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-soft"
                    >
                        {SUPPORTED_COUNTRIES.map((c) => (
                            <option key={c.code} value={c.code}>{c.flag} {c.label}</option>
                        ))}
                    </select>
                    <span className="text-xs text-[#6E7A74]">
                        {countryFlag} {countryLabel} · plus international partners
                    </span>
                </div>
            </header>

            {/* "What we're working on for you" — categories showcase. Always
                visible (even when the partner list is sparse) so caregivers
                see the full intent of the directory at a glance. */}
            <section
                aria-label="Future community partners we're building toward"
                data-testid="coming-soon-categories"
                className="ml-card p-6 md:p-8 bg-teal-soft/30 border border-teal-light mb-9"
            >
                <div className="flex items-start gap-2 mb-1">
                    <span className="text-[10px] uppercase tracking-wider font-semibold text-teal-dark bg-white px-2 py-0.5 rounded-2xl inline-flex items-center gap-1">
                        <Sparkles size={11} strokeWidth={1.8} /> Coming soon
                    </span>
                </div>
                <h2 className="font-heading text-2xl md:text-3xl text-[#12776F] mt-2">
                    Future community partners we&apos;re lining up for you.
                </h2>
                <p className="text-sm md:text-base text-[#4C5551] mt-2 leading-relaxed max-w-3xl">
                    Caregiving isn&apos;t one problem — it&apos;s eight. So we&apos;re building
                    partnerships across eight kinds of help, so when you&apos;re depleted,
                    overwhelmed, or just plain hungry, there&apos;s already someone in
                    your corner.
                </p>

                <ul
                    className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-6"
                    data-testid="coming-soon-grid"
                >
                    {COMING_CATEGORIES.map(({ id, label, Icon, tagline }) => {
                        const count = partners.filter(p => p.category === id).length;
                        return (
                            <li key={id}>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setSelected(id);
                                        // Defer scroll until the filter re-renders so we land on the right card stack.
                                        requestAnimationFrame(() => {
                                            partnersRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
                                        });
                                    }}
                                    data-testid={`coming-soon-${id}`}
                                    aria-label={`Filter partners by ${label}${count > 0 ? ` — ${count} listed` : ""}`}
                                    className="w-full text-left bg-white rounded-2xl p-4 border border-cream-dark hover:border-teal-dark hover:shadow-sm transition-all"
                                >
                                    <div className="flex items-center gap-2.5 mb-1.5">
                                        <span className="w-9 h-9 rounded-2xl bg-teal-soft flex items-center justify-center shrink-0">
                                            <Icon size={16} strokeWidth={1.7} className="text-teal-dark" />
                                        </span>
                                        <h3 className="font-heading text-base text-[#1F2624] leading-tight flex-1">
                                            {label}
                                        </h3>
                                        {count > 0 && (
                                            <span className="text-[10px] font-semibold text-teal-dark bg-teal-soft px-2 py-0.5 rounded-2xl shrink-0">
                                                {count}
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-xs text-[#4C5551] leading-relaxed">{tagline}</p>
                                </button>
                            </li>
                        );
                    })}
                </ul>

                <p className="mt-6 text-sm text-[#4C5551] inline-flex items-center gap-1.5">
                    <Heart size={14} strokeWidth={1.8} className="text-coral-dark" />
                    Each new partner gets vetted by Ashlee personally — your time is too precious for filler.
                </p>
            </section>

            {/* Filter chips */}
            <div
                ref={partnersRef}
                className="flex flex-wrap gap-2 mb-7 scroll-mt-6"
                data-testid="community-partners-filters"
                role="tablist"
                aria-label="Filter community partners"
            >
                <FilterChip
                    active={selected === "all"}
                    onClick={() => setSelected("all")}
                    testid="filter-all"
                >
                    All ({partners.length})
                </FilterChip>
                {counts.live > 0 && (
                    <FilterChip
                        active={selected === "live"}
                        onClick={() => setSelected("live")}
                        testid="filter-live"
                        tone="live"
                    >
                        Live now ({counts.live})
                    </FilterChip>
                )}
                {counts.coming_soon > 0 && (
                    <FilterChip
                        active={selected === "coming_soon"}
                        onClick={() => setSelected("coming_soon")}
                        testid="filter-coming-soon"
                        tone="coming"
                    >
                        Coming soon ({counts.coming_soon})
                    </FilterChip>
                )}
                {categories.map(c => {
                    const count = partners.filter(p => p.category === c.id).length;
                    if (count === 0) return null;
                    return (
                        <FilterChip
                            key={c.id}
                            active={selected === c.id}
                            onClick={() => setSelected(c.id)}
                            testid={`filter-cat-${c.id}`}
                        >
                            {c.label} ({count})
                        </FilterChip>
                    );
                })}
            </div>

            {/* Body */}
            {loading ? (
                <p className="text-sm text-[#6E7A74] inline-flex items-center gap-2">
                    <Loader2 size={14} className="animate-spin" /> Loading partners…
                </p>
            ) : filtered.length === 0 ? (
                <EmptyState totalCount={partners.length} />
            ) : (
                <ul
                    className="grid grid-cols-1 sm:grid-cols-2 gap-4"
                    data-testid="community-partners-list"
                >
                    {filtered.map(p => <PartnerCard key={p.id} partner={p} categories={categories} />)}
                </ul>
            )}

            {/* Suggestion footer */}
            <section className="ml-card mt-10 p-6 md:p-7 bg-yellow-soft/40 border border-yellow-soft" data-testid="community-partners-suggest">
                <h2 className="font-heading text-xl text-[#1F2624]">Know a place that should be on this list?</h2>
                <p className="text-sm text-[#4C5551] mt-1 leading-relaxed">
                    If a spa, gym, therapist, meal service, or community group has been a
                    lifeline for you — Ashlee would love to know. Reach out and we&apos;ll explore
                    a partnership so other caregivers can find them too.
                </p>
                <a
                    href={`mailto:ashlee@asvmedicalservices.com?subject=${encodeURIComponent("Suggested community partner")}&body=${encodeURIComponent("Hi Ashlee,\n\nI think this place should be a Memory Lane community partner:\n\n- Name:\n- What they do:\n- Why it helps caregivers:\n- Their website / contact:\n\nThanks!\n")}`}
                    data-testid="community-partners-suggest-cta"
                    className="ml-btn-secondary mt-4 inline-flex items-center gap-2"
                >
                    Suggest a partner
                </a>
            </section>
        </div>
    );
}

function FilterChip({ active, onClick, children, testid, tone }) {
    let cls = "bg-white text-[#4C5551] border-[#E2E8F0] hover:bg-cream-dark";
    if (active) {
        if (tone === "coming") cls = "bg-yellow-soft text-yellow-dark border-yellow-soft";
        else if (tone === "live") cls = "bg-stage_early text-stage_early-dark border-stage_early";
        else cls = "bg-teal text-white border-teal";
    }
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testid}
            role="tab"
            aria-selected={active}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-2xl text-sm font-medium border transition-colors ${cls}`}
        >
            {children}
        </button>
    );
}

function PartnerCard({ partner, categories }) {
    const catLabel = categories.find(c => c.id === partner.category)?.label || partner.category;
    const isLive = partner.status === "live";
    return (
        <li
            data-testid={`community-partner-${partner.id}`}
            className={`ml-card p-5 md:p-6 flex flex-col ${isLive ? "bg-white" : "bg-cream-light"}`}
        >
            <div className="flex items-start gap-3 justify-between mb-2">
                <h3 className="font-heading text-lg text-[#1F2624] leading-tight">{partner.name}</h3>
                {isLive ? (
                    <span className="shrink-0 text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-2xl bg-stage_early/60 text-stage_early-dark">
                        Live
                    </span>
                ) : (
                    <span className="shrink-0 text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-2xl bg-yellow-soft text-yellow-dark">
                        Coming soon
                    </span>
                )}
            </div>
            <p className="text-xs text-[#6E7A74] inline-flex items-center gap-3 mb-2 flex-wrap">
                <span className="inline-flex items-center gap-1"><Tag size={11} strokeWidth={1.8} /> {catLabel}</span>
                {partner.region && (
                    <span className="inline-flex items-center gap-1"><MapPin size={11} strokeWidth={1.8} /> {partner.region}</span>
                )}
            </p>
            {partner.description && (
                <p className="text-sm text-[#4C5551] leading-relaxed mb-3 flex-1">{partner.description}</p>
            )}
            {partner.perk && (
                <p className="text-xs font-semibold text-stage_early-dark bg-stage_early/30 px-3 py-1.5 rounded-2xl inline-block self-start mb-3">
                    💛 {partner.perk}
                </p>
            )}
            {isLive && partner.url && (
                <a
                    href={partner.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`community-partner-link-${partner.id}`}
                    className="text-sm font-medium text-teal-dark hover:underline inline-flex items-center gap-1 self-start mt-auto"
                >
                    Visit <ExternalLink size={12} strokeWidth={1.8} />
                </a>
            )}
        </li>
    );
}

function EmptyState({ totalCount }) {
    if (totalCount === 0) {
        return (
            <div
                className="ml-card p-7 md:p-10 bg-cream-light text-center max-w-2xl mx-auto"
                data-testid="community-partners-empty"
            >
                <div className="w-14 h-14 rounded-2xl bg-teal-soft mx-auto mb-4 flex items-center justify-center">
                    <Sparkles size={22} strokeWidth={1.8} className="text-teal-dark" />
                </div>
                <h2 className="font-heading text-xl text-[#1F2624]">Soon, this page will glow.</h2>
                <p className="text-sm text-[#4C5551] mt-2 leading-relaxed">
                    Ashlee is actively building partnerships with spas, gyms, mental-wellness
                    providers, virtual doctors, meal services, and respite teams — so when
                    you&apos;re depleted, you&apos;ll know exactly where to turn.
                    <br /><br />
                    Check back in a couple weeks. Or, if you know a place that should be here,
                    use the suggest box below.
                </p>
            </div>
        );
    }
    return (
        <p className="text-sm text-[#6E7A74] py-6">No partners match that filter yet.</p>
    );
}
